import React, { useMemo } from "react";
import { motion } from "framer-motion";

// ── Animation presets ──────────────────────────────────────
export const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.25 },
  transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
};

// ── Logo ───────────────────────────────────────────────────
const LOGO_URL = "https://customer-assets.emergentagent.com/job_939db7ea-c2c7-45d9-9e53-49b95e039d31/artifacts/73l9j786_imaani%20captial%20wide.png";

export function Logo({ size = "default", showTagline = true }) {
  const sizeClasses = { small: "h-10", default: "h-14", large: "h-20" };
  return (
    <div className="flex flex-col items-start">
      <img src={LOGO_URL} alt="Imaani Capital" className={`${sizeClasses[size]} w-auto object-contain`} />
      {showTagline && (
        <p className="mt-1 text-xs uppercase tracking-[0.35em] text-gold/80 font-light">Capital &bull; Growth &bull; Legacy</p>
      )}
    </div>
  );
}

// ── Particle System ────────────────────────────────────────
export function ParticleField() {
  const particles = useMemo(() =>
    Array.from({ length: 50 }, (_, i) => ({
      id: i, x: Math.random() * 100, y: Math.random() * 100,
      size: Math.random() * 3 + 1, duration: Math.random() * 20 + 15, delay: Math.random() * 5,
    })), []);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map(p => (
        <motion.div key={p.id} className="absolute rounded-full"
          style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.size, height: p.size,
            background: `radial-gradient(circle, rgba(229,201,139,${0.3 + Math.random() * 0.4}) 0%, transparent 70%)` }}
          animate={{ y: [0, -30, 0], x: [0, Math.random() * 20 - 10, 0], opacity: [0.3, 0.8, 0.3], scale: [1, 1.5, 1] }}
          transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: "easeInOut" }} />
      ))}
    </div>
  );
}

export function AmbientGlow() {
  return (
    <>
      <motion.div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full opacity-20"
        style={{ background: "radial-gradient(circle, rgba(16,185,129,0.4) 0%, transparent 70%)", filter: "blur(80px)" }}
        animate={{ scale: [1, 1.2, 1], x: [0, 50, 0], y: [0, 30, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }} />
      <motion.div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full opacity-15"
        style={{ background: "radial-gradient(circle, rgba(229,201,139,0.5) 0%, transparent 70%)", filter: "blur(100px)" }}
        animate={{ scale: [1.2, 1, 1.2], x: [0, -40, 0], y: [0, -20, 0] }}
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }} />
    </>
  );
}

// ── Luxury Background (cream with texture) ─────────────────
export function LuxuryBackground() {
  return (
    <>
      <div className="absolute inset-0 bg-cream" />
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L60 30L30 60L0 30z' fill='none' stroke='%23065F46' stroke-width='0.5'/%3E%3C/svg%3E")`, backgroundSize: '60px 60px' }} />
    </>
  );
}

// ── Section ────────────────────────────────────────────────
export function Section({ eyebrow, title, description, children }) {
  return (
    <section className="relative z-10 mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
      <motion.div {...fadeUp} className="mb-8 sm:mb-12 max-w-3xl">
        {eyebrow && <p className="text-xs sm:text-sm uppercase tracking-[0.4em] text-emerald-700 font-semibold">{eyebrow}</p>}
        <h2 className="mt-3 text-2xl font-bold tracking-tight text-[#0A0A0A] sm:mt-4 sm:text-4xl md:text-5xl">{title}</h2>
        {description && <p className="mt-4 text-base leading-7 text-[#333333] sm:mt-6 sm:text-lg sm:leading-8">{description}</p>}
      </motion.div>
      {children}
    </section>
  );
}

// ── Page Hero (dark emerald) ───────────────────────────────
export function PageHero({ eyebrow, title, description, rightContent, compact = false }) {
  return (
    <section className={`relative overflow-hidden ${compact ? "" : "border-b border-emerald-900/30"}`}>
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-950 via-emerald-900 to-emerald-950" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(229,201,139,0.08),transparent_60%)]" />
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L60 30L30 60L0 30z' fill='none' stroke='%23E5C98B' stroke-width='0.5'/%3E%3C/svg%3E")`, backgroundSize: '60px 60px' }} />
      <div className={`relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${compact ? "py-6 sm:py-8" : "py-12 sm:py-20 lg:py-28"}`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className={compact ? "" : "max-w-3xl"}>
            {eyebrow && (
              <p className={`uppercase font-bold ${compact ? "text-[10px] tracking-[0.4em] text-[#E5C98B] mb-2" : "mb-4 text-sm tracking-[0.45em] text-gold"}`}>{eyebrow}</p>
            )}
            <h1 className={`font-bold leading-tight tracking-tight text-white ${compact ? "text-2xl" : "text-2xl sm:text-4xl md:text-5xl lg:text-6xl"}`}>{title}</h1>
            {description && <p className={`text-white/50 ${compact ? "mt-1 text-sm" : "mt-4 text-base leading-7 sm:mt-6 sm:text-lg sm:leading-8 md:text-xl"}`}>{description}</p>}
          </div>
          {rightContent && <div className="shrink-0">{rightContent}</div>}
        </div>
      </div>
    </section>
  );
}

// ── Card (premium with emerald accent) ─────────────────────
export function Card({ children, className = "", hover = true }) {
  return (
    <motion.div whileHover={hover ? { y: -6, scale: 1.01 } : {}} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className={`relative group rounded-2xl overflow-hidden bg-white border border-cream-warm shadow-[0_2px_20px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_40px_rgba(6,95,70,0.12)] hover:border-emerald-200 transition-all duration-300 ${className}`}>
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-emerald-600 via-gold to-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-l-2xl" />
      <div className="relative z-10 p-5 sm:p-8">{children}</div>
    </motion.div>
  );
}

// ── GoldButton ─────────────────────────────────────────────
export function GoldButton({ children, onClick, variant = "primary", type = "button", className = "", ...props }) {
  const base = "relative overflow-hidden rounded-full font-bold transition-all duration-300 transform hover:scale-105 active:scale-95";
  if (variant === "primary") {
    return (
      <motion.button type={type} onClick={onClick} className={`${base} px-8 py-4 text-sm ${className}`}
        whileHover={{ boxShadow: "0 0 40px rgba(6,95,70,0.3)" }} whileTap={{ scale: 0.95 }} {...props}>
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-800 via-emerald-700 to-emerald-800" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/10" />
        <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12"
          animate={{ x: ["-200%", "200%"] }} transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }} />
        <span className="relative z-10 text-white tracking-wide inline-flex items-center gap-1">{children}</span>
      </motion.button>
    );
  }
  return (
    <motion.button type={type} onClick={onClick}
      className={`${base} px-8 py-4 text-sm border-2 border-emerald-700/40 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 hover:border-emerald-700/60 ${className}`}
      whileHover={{ boxShadow: "0 0 30px rgba(6,95,70,0.15)" }} whileTap={{ scale: 0.95 }} {...props}>
      {children}
    </motion.button>
  );
}

// ── Form Input (dark context — auth/dashboard) ─────────────
export const inputClass = "w-full rounded-xl border border-emerald-800/30 bg-emerald-950/50 px-4 py-3.5 text-cream outline-none transition placeholder:text-cream/30 focus:border-gold/50 focus:ring-2 focus:ring-gold/20 backdrop-blur-sm";
export const selectClass = "w-full rounded-xl border border-emerald-800/30 bg-emerald-950/50 px-4 py-3.5 text-cream outline-none focus:border-gold/50 focus:ring-2 focus:ring-gold/20 backdrop-blur-sm";
export const labelClass = "mb-2 block text-sm text-gold/80 font-medium";

// ── Form Input (light context — public pages) ──────────────
export const inputClassLight = "w-full rounded-xl border border-cream-warm bg-white px-4 py-3.5 text-[#0A0A0A] outline-none transition placeholder:text-[#999] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 shadow-sm";
export const selectClassLight = "w-full rounded-xl border border-cream-warm bg-white px-4 py-3.5 text-[#0A0A0A] outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 shadow-sm";
export const labelClassLight = "mb-2 block text-sm text-[#0A0A0A] font-semibold";
